import { useContext, useState } from 'react';
import { CreateTransactionsContext, CreateTransactionsValues } from '../contexts/CreateTransactionsContext';
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, useTheme, Stack, SvgIcon, TextField, useMediaQuery } from '@mui/material';
import { CreateTransactionsItemsTable } from './CreateTransactionsItemsTable';
import { CreateTransactionsTopForms } from './CreateTransactionsTopForms';
import { CreateTransactionsChangeCostConfirmDialog } from './CreateTransactionsChangeCostConfirmDialog';
import { applyRoundAndFormat } from '../../../../../../utils';
import { ConfirmationDialog } from '../../../../../../components/shared/dialogs/ConfirmationDialog';
import FileCopy from '@mui/icons-material/FileCopy';
import WarningIcon from '@mui/icons-material/Warning';
import { Plus } from '../../../../../../assets';
import { CloseDialogAction } from '../../../../../../components/shared/dialogs/CloseDialogAction';

export const MainDialog = () => {
  const { successDismissButtonHandler,
    openModal,
    totalReceiptValue,
    t,
    currencyCode,
    hasNewChanges,
    handleDuplicateDialogClose,
    isDuplicateModalOpen,
    createTransactions,
    isTypeChangeModalOpen,
    closeTransactionTypeChangeModal,
    setIsDuplicateModalOpen,
    isSubmittedSuccess,
    onCancel,
    changeType,
    isFormInvalid,
    handleSubmit,
    isDisableActions,
    isError,
    errorDismissButtonHandler,
    isProcessingSubmit,
    createErrorMessage,
    note,
    handleNoteChange } = useContext(CreateTransactionsContext) as CreateTransactionsValues;
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [showCancelConfirmationModal, setShowCancelConfirmationModal] = useState<boolean>(false);

  const handleCancel = () => {
    if (isProcessingSubmit) return;

    if (hasNewChanges && !isSubmittedSuccess) {
      if (!showCancelConfirmationModal) {
        setShowCancelConfirmationModal(true);
      }
    }
    else {
      successDismissButtonHandler();
      onCancel();
    }
  };

  const handleCancelConfirmation = (confirmed: boolean) => {
    setShowCancelConfirmationModal(false);

    if (confirmed) {
      successDismissButtonHandler();
      onCancel();
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth={'lg'}
      fullScreen={isMobile}
      onClose={handleCancel}
      open={openModal}
    >
      <DialogTitle>
        {t('title')}
        <CloseDialogAction onCancel={handleCancel} />
      </DialogTitle>

      <DialogContent>
        <Grid container
          spacing={5}>
          <CreateTransactionsTopForms />
          <CreateTransactionsItemsTable />
          <Grid item
            xs={12}>
            <FormControl fullWidth
              size='small'>
              <TextField id="textfeild-note"
                label={t('note')}
                multiline
                rows={4}
                size='small'
                disabled={isDisableActions}
                value={note}
                onChange={handleNoteChange} />
            </FormControl>
          </Grid>
          {totalReceiptValue > 0 && <Grid item
            xs={12}>
            <Alert severity='info'>
              {t('totalReceiptValue') + ' ' + applyRoundAndFormat(totalReceiptValue, currencyCode)}
            </Alert>
          </Grid>
          }
          <Grid item
            xs={12}>
            {isError && <Alert severity="error"
              onClose={errorDismissButtonHandler}>
              {t('errorAlert')} - {createErrorMessage}
            </Alert>}
            {isSubmittedSuccess && <Alert severity="success">
              {t('successAlert')}
            </Alert>}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          spacing={4}>
          <Button variant='secondary'
            size="lg"
            onClick={handleCancel}>
            {t('cancel')}
          </Button>
          <Button
            variant='primary'
            size="lg"
            disabled={isFormInvalid() || isDisableActions || isProcessingSubmit || isSubmittedSuccess}
            onClick={handleSubmit}
            startIcon={isProcessingSubmit ? <CircularProgress size={20}
              sx={{ ml: '-4px', mr: '4px' }} /> : <SvgIcon><Plus /></SvgIcon>}>
            {t('createTransactions')}
          </Button>
        </Stack>
      </DialogActions>

      <ConfirmationDialog open={isDuplicateModalOpen}
        message={t('duplicateDialog.message1')}
        onConfirm={() => {
          createTransactions();
          handleDuplicateDialogClose();
        }}
        onCancel={() => setIsDuplicateModalOpen(false)}
        icon={<FileCopy />}
        iconBgColor={theme.palette.custom.blue}
        iconColor={theme.palette.custom.blue}
        title={t('duplicateDialog.title')}
        variant='primary'
        confirmButtonText={t('duplicateDialog.confirm')}
        cancelButtonText={t('cancel')}
      />

      <ConfirmationDialog open={isTypeChangeModalOpen}
        message={t('confirmType.message')}
        onConfirm={() => {
          changeType();
          closeTransactionTypeChangeModal();
        }}
        onCancel={closeTransactionTypeChangeModal}
        icon={<WarningIcon />}
        iconBgColor={theme.palette.warning}
        iconColor={theme.palette.warning}
        title={t('confirmType.title')}
        variant='primary'
        confirmButtonText={t('confirmType.continue')}
        cancelButtonText={t('cancel')}
      />

      <CreateTransactionsChangeCostConfirmDialog></CreateTransactionsChangeCostConfirmDialog>

      <ConfirmationDialog open={showCancelConfirmationModal}
        message={t('leaveModalContent')}
        onConfirm={() => {
          handleCancelConfirmation(true);
        }}
        onCancel={() => handleCancelConfirmation(false)}
        icon={<WarningIcon />}
        iconBgColor={theme.palette.warning}
        iconColor={theme.palette.warning}
        title={t('leaveModalTitle')}
        variant='primary'
        confirmButtonText={t('confirmType.continue')}
        cancelButtonText={t('cancel')}
      />

    </Dialog>
  );
};

